/**
 * Deleting a room gives its neighbours their walls back (PRP-SKETCH-FIELD-3 W5,
 * DD-SF3.16). A missing wall is an opening between two rooms; with one room gone it
 * would open onto nothing (and export as a 0 in outside wall with the whole wall
 * open). So before a room is deleted, every missing wall linked to it is undone
 * from the survivor's side:
 *
 * - the survivor's copy of the missing wall goes;
 * - a survivor that absorbed the wall (J1 `missing.absorbed`) gives it back, at the
 *   thickness it stored (`giveBack`, as Restore wall does), with the openings the
 *   removal took off its side put back;
 * - a 0 in wall with nothing absorbed (a split line) becomes a wall at the plan's
 *   interior default thickness, the room's face staying where it is;
 * - the survivor gets the check-dimensions note naming the deleted room.
 */
import { POLICY } from './policy';
import { findRoom, tidy } from './geom';
import { adjacent } from './shared';
import { setWallIn, wallIn } from './thickness';
import { noteCheckDims } from './walls';
import { IN_TOL, giveBack, putBack } from './wallRemove';
import type { Plan, Room } from './types';

const fmtIn = (n: number) => `${Number(n.toFixed(2))}"`;

/** Undo every missing wall between `dead` and the other rooms. Returns the rooms changed. */
export function releaseMissingWalls(plan: Plan, dead: Room, now: string = new Date().toISOString()): string[] {
  const changed: string[] = [];
  for (const q of plan.rooms) {
    if (q === dead) continue;
    for (const o of [...q.openings]) {
      if (o.kind !== 'missing' || o.link == null) continue;
      const p = dead.openings.find((x) => x.link === o.link);
      if (!p) continue;
      const rec = o.missing?.absorbed ?? p.missing?.absorbed;
      if (!rec && !adjacent(plan, q, o)) continue; // dragged apart: two openings (F7), not a wall between them
      const mine = rec?.find((x) => x.roomId === q.id);
      const taken = o.missing?.removedOpenings ?? p.missing?.removedOpenings ?? [];
      q.openings = q.openings.filter((x) => x !== o);
      const tDefault = plan.defaults.interiorWallIn ?? POLICY.interior_wall_in;
      let back = wallIn(q, o.edgeStart);
      if (mine && mine.distanceIn > IN_TOL) {
        back = mine.wallIn ?? mine.distanceIn;
        giveBack(q, o.edgeStart, mine.distanceIn, back, mine);
      } else if (back <= IN_TOL) {
        back = tDefault;
        setWallIn(q, o.edgeStart, back);
      }
      putBack(plan, taken, (rid) => (rid === q.id ? o.edgeStart : undefined), q.id);
      noteCheckDims(q, `${dead.name} was deleted: the missing wall to it is a wall again (${fmtIn(back)})`, now);
      if (!changed.includes(q.id)) changed.push(q.id);
    }
  }
  tidy(plan);
  return changed;
}

/** The room `rid` with its missing walls released (for `deleteRoom`). */
export function releaseBeforeDelete(plan: Plan, rid: string, now?: string): string[] {
  const r = findRoom(plan, rid);
  return r ? releaseMissingWalls(plan, r, now) : [];
}
